import type { AnalyticsEventPayload, AnalyticsProvider, AnalyticsUserProperties } from '@/services/analytics';
import { bootstrap, event, set as setGtagParams, setOptions } from 'vue-gtag';
import type { PluginOptions } from 'vue-gtag';
import { buildAnalyticsEventContext } from '@/services/analytics/eventContext';
import { sanitizeTrackingUrl } from '@/utils/tracking.utils';
import type { ICookieConsent } from '@/utils/cookieConsent.utils';

const GA4_MEASUREMENT_ID = 'G-PF923DLJQE';

type GoogleConsentState = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function buildConsentPayload(
  analytics: GoogleConsentState,
  marketing: GoogleConsentState,
): Record<string, GoogleConsentState> {
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
  };
}

function installGtagStub(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
}

export class Ga4Analytics implements AnalyticsProvider {
  public readonly name = 'ga4';
  public readonly consentCategories = ['analytics'] as const;
  private consent: ICookieConsent | null = null;
  private initialized = false;
  private readonly measurementId = GA4_MEASUREMENT_ID;
  private lastAppliedConsentState: string | undefined;
  private userProperties: AnalyticsUserProperties | undefined;

  public init(): Promise<void> {
    installGtagStub();
    window.gtag?.('consent', 'default', buildConsentPayload('denied', 'denied'));
    this.initialized = true;
    this.setConsent(this.consent);
    const options: PluginOptions = {
      enabled: true,
      pageTrackerEnabled: false,
      config: {
        id: this.measurementId,
        params: {
          cookie_domain: 'auto',
          send_page_view: false,
          ...this.buildPageParameters(),
          ...(import.meta.env.DEV ? { debug_mode: true } : {}),
        },
      },
    };
    setOptions(options);
    return bootstrap().then((result) => {
      // vue-gtag resolves script-load failures with the browser's error event.
      if (result instanceof Error || result instanceof Event) throw result;
    });
  }

  public trackPageview(path?: string, payload?: AnalyticsEventPayload, properties = this.userProperties): void {
    this.trackEvent('page_view', { ...payload, ...this.buildPageParameters(path) }, properties);
  }

  public trackEvent(name: string, payload?: AnalyticsEventPayload, properties = this.userProperties): void {
    event(name.replace(/\./g, '_'), {
      ...this.buildPageParameters(),
      ...payload,
      ...buildAnalyticsEventContext(properties),
      ...(properties ? { user_properties: properties } : {}),
      send_to: this.measurementId,
    });
  }

  private buildPageParameters(path?: string): AnalyticsEventPayload {
    const href = window.location.href;
    if (!href) return {};
    return {
      page_location: sanitizeTrackingUrl(path ? new URL(path, href).href : href),
      page_referrer: sanitizeTrackingUrl(typeof document === 'undefined' ? '' : document.referrer),
      ...(path ? { page_path: path } : {}),
    };
  }

  public setUserId(userId: string | null): void {
    setGtagParams({ user_id: userId });
  }

  public setUserProperties(properties: AnalyticsUserProperties): void {
    this.userProperties = { ...properties };
    window.gtag?.('set', 'user_properties', this.userProperties);
  }

  public setConsent(consent: ICookieConsent | null): void {
    this.consent = consent;
    if (!this.initialized) return;
    const analytics: GoogleConsentState = consent?.analytics ? 'granted' : 'denied';
    const marketing: GoogleConsentState = consent?.marketing ? 'granted' : 'denied';
    const nextState = `${analytics}:${marketing}`;
    if (this.lastAppliedConsentState === nextState) return;
    window.gtag?.('set', {
      allow_google_signals: consent?.marketing === true,
      allow_ad_personalization_signals: consent?.marketing === true,
    });
    window.gtag?.('consent', 'update', buildConsentPayload(analytics, marketing));
    this.lastAppliedConsentState = nextState;
  }
}
